import { USER_PERMISSIONS } from "Constants";
import { NavBarLink } from "Models/nav.bar.link";
import { Resources, RolePermissionsResponse } from "Models/resources";
import { ResourceCanEnum, ResourceName, ResourceObjectTypeEnum } from "Util/enums/accessRule";
import { links } from "Util/links";
import { checkResourceCan } from "Util/permissions";
import { isMoving } from "Util/validation";
import { useAppState } from "Context/StateContext";
import { useEffect, useState } from "react";
import { NavBarLinks } from "Types";
import { useHistory } from "react-router";

const normalizePath = (p?: string) => (p || "").toLowerCase().replace(/\/+$/, "");
const pathMatches = (a?: string, b?: string) => {
  const A = normalizePath(a);
  const B = normalizePath(b);
  if (!A || !B) return false;
  return A.startsWith(B) || B.startsWith(A);
};

const matchesResource = (navBarLink: NavBarLink, resourceNameRaw?: string) => {
  const resourceName = (resourceNameRaw || "").toLowerCase().trim();
  if (!resourceName) return false;

  if (navBarLink.resource && navBarLink.resource.toLowerCase().trim() === resourceName) {
    return true;
  }

  const linkPath = (navBarLink.path || "").toLowerCase();
  return linkPath.includes(resourceName);
};

export const useResourcePermissionsActions = () => {
  const { currentUser, setPathPermissions, userPermissions } = useAppState();

  const [navBarLinks, setNavBarLinks] = useState<NavBarLinks>();
  const [showButtonNetwork, setShowButtonNetwork] = useState<boolean>(false);
  const [navBarLinkSelected, setNavBarLinkSelected] = useState<NavBarLink>();
  const [groupSelected, setGroupSelected] = useState<string>();
  const history = useHistory();

  const getInitialUserResources = (): RolePermissionsResponse[] => {
    const permissions = localStorage.getItem(USER_PERMISSIONS);

    if (permissions) {
      return JSON.parse(permissions)?.filter(
        (permission: RolePermissionsResponse) =>
          permission?.objectType === ResourceObjectTypeEnum.RESOURCE
      );
    }

    if (userPermissions) {
      return userPermissions?.filter(
        (permission: RolePermissionsResponse) =>
          permission?.objectType === ResourceObjectTypeEnum.RESOURCE
      );
    }

    return [];
  };

  const loadResources = (updateNavBarLinks?: boolean, pathname?: string) => {
    const navBarLinksFiltered: { [group: string]: Array<NavBarLink> } = {};

    const PATHNAME = pathname || location.pathname;
    const PATHNAME_NORM = normalizePath(PATHNAME);

    if (currentUser?.company?.profile) {
      const linksByCompany = links[currentUser?.company?.profile?.initials];
      const groups = linksByCompany ? Object.keys(linksByCompany) : [];
      const isMovingCompany = isMoving(currentUser?.company);

      const pathPermissionsSet = new Set<string>();
      let pathPermissions: { route: string; isShow: boolean }[] = [];
      const userResources: RolePermissionsResponse[] = getInitialUserResources();

      if (!groups?.length) return;

      groups.forEach((group) => {
        const groupLinks = linksByCompany[group] ?? [];
        const navBarLinksInResource: Array<NavBarLink> = [];

        groupLinks.forEach((navBarLink) => {
          if (!currentUser) return;

          if (isMovingCompany && userResources?.length) {
            userResources.forEach((resource) => {
              const resourceName = resource?.objectDetails?.name;
              if (!resourceName) return;

              if (
                resourceName === "Network" &&
                checkResourceCan(userResources, ResourceName.NETWORK, ResourceCanEnum.VIEW)
              ) {
                setShowButtonNetwork(true);
              }

              if (resource?.canView === 1 && matchesResource(navBarLink, resourceName)) {
                if (!pathPermissionsSet.has(navBarLink.path)) {
                  if ((navBarLink?.key || "").toLowerCase() === resourceName.toLowerCase()) {
                    pathPermissionsSet.add(navBarLink.path);
                    pathPermissions.push({
                      route: navBarLink.path,
                      isShow: true,
                    });
                  }
                }

                if (resourceName === "Route Planner" && !currentUser?.company?.useRoutePlanner) {
                  navBarLink.show = false;
                }

                if (navBarLink.show !== false) {
                  if (!navBarLinksInResource.some((n) => n?.key === navBarLink?.key)) {
                    navBarLinksInResource.push(navBarLink);
                  }
                }
              }
            });
          }

          if (!isMovingCompany && currentUser?.resources?.length) {
            currentUser.resources.forEach((resource) => {
              const resourceName = resource?.name;
              if (!resourceName) return;

              if (resourceName === "Network") setShowButtonNetwork(true);
              if (resourceName.toLowerCase() === "suppliers") return;

              if (resource?.roles_resources?.view && matchesResource(navBarLink, resourceName)) {
                if (resourceName === "Route Planner" && !currentUser?.company?.useRoutePlanner) {
                  navBarLink.show = false;
                }

                if (navBarLink.show !== false) {
                  if (!navBarLinksInResource.some((n) => n?.key === navBarLink?.key)) {
                    navBarLinksInResource.push(navBarLink);
                  }
                }
              }
            });
          }

          if (pathMatches(PATHNAME_NORM, navBarLink.path)) {
            setNavBarLinkSelected(navBarLink);
            setGroupSelected(group);
          }
        });

        if (navBarLinksInResource.length) {
          navBarLinksFiltered[group] = navBarLinksInResource;
        }
      });

      setPathPermissions(pathPermissions);
    }

    if (updateNavBarLinks) setNavBarLinks(navBarLinksFiltered);
  };

  useEffect(() => {
    loadResources(true);

    const unListener = history.listen((listener) => {
      loadResources(false, listener.pathname);
    });

    return () => unListener();
  }, [userPermissions]);

  return {
    navBarLinks,
    showButtonNetwork,
    navBarLinkSelected,
    groupSelected,
    setNavBarLinkSelected,
    setGroupSelected,
    loadResources,
  };
};
